/**
 * Shipping Calculator for Shopify Product Page
 * Integrates with ViaCEP for city/state lookup and Shopify AJAX API for rates.
 */
class ShippingCalculator {
  constructor() {
    this.container = document.querySelector('.product-page__shipping-calc');
    if (!this.container) return;

    this.input = this.container.querySelector('.shipping-calc__input');
    this.button = this.container.querySelector('.shipping-calc__submit');
    this.resultsContainer = this.container.querySelector('.shipping-calc__results');
    this.sectionId = this.container.dataset.sectionId;

    this.init();
  }

  init() {
    // Mask for CEP (00000-000)
    this.input.addEventListener('input', (e) => {
      let val = e.target.value.replace(/\D/g, '');
      if (val.length > 5) {
        val = val.substring(0, 5) + '-' + val.substring(5, 8);
      }
      e.target.value = val;
    });

    // Enter key trigger
    this.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.calculateShipping();
      }
    });

    this.button.addEventListener('click', () => this.calculateShipping());
  }

  async calculateShipping() {
    const cep = this.input.value.replace(/\D/g, '');
    if (cep.length !== 8) {
      this.showError('Por favor, informe um CEP válido com 8 dígitos.');
      return;
    }

    this.setLoading(true);

    // Clear and hide results immediately to avoid "fake/old data" flash
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.setAttribute('hidden', '');

    try {
      // 1. Get City/State from ViaCEP (Shopify needs this to avoid 422 error)
      const viaCepResponse = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const viaCepData = await viaCepResponse.json();

      if (viaCepData.erro) {
        throw new Error('CEP não encontrado.');
      }

      const province = viaCepData.uf;
      const city = viaCepData.localidade;

      // 2. Detect current variant ID
      const variantInput = document.querySelector(`#product-variant-input-${this.sectionId}`);
      const variantId = variantInput ? variantInput.value : null;

      if (!variantId) {
        throw new Error('Variante não identificada.');
      }

      // 3. Check current cart status
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      let itemWasAdded = false;
      const productInCart = cart.items.find(item => item.id == variantId);

      // 4. Temporarily add product if not in cart
      if (!productInCart) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        itemWasAdded = true;
      }

      // 5. Fetch shipping rates from Shopify API
      // We send the CEP with hyphen as some carriers require it
      const zipWithHyphen = `${cep.substring(0, 5)}-${cep.substring(5, 8)}`;
      const ratesUrl = `/cart/shipping_rates.json?shipping_address[zip]=${zipWithHyphen}&shipping_address[country]=Brazil&shipping_address[province]=${province}&shipping_address[city]=${city}`;

      const ratesResponse = await fetch(ratesUrl);
      const ratesData = await ratesResponse.json();

      if (!ratesResponse.ok) {
        const errorMsg = ratesData.zip ? `CEP: ${ratesData.zip[0]}` : 'Erro ao calcular frete.';
        throw new Error(errorMsg);
      }

      // 6. Remove temporary item if it was added
      if (itemWasAdded) {
        // We use change.js or update.js to be safer, but clear.js is too aggressive.
        // For simplicity and speed in calculation, we remove that specific variant.
        const cartUpdate = { updates: {} };
        cartUpdate.updates[variantId] = productInCart ? productInCart.quantity : 0;
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cartUpdate)
        });
      }

      // 7. Render rates and destination
      this.renderRates(ratesData.shipping_rates, `${city} - ${province}`);

    } catch (error) {
      console.error('Shipping Calculator Error:', error);
      this.showError(error.message || 'Ocorreu um erro ao calcular o frete. Tente novamente.');
    } finally {
      this.setLoading(false);
    }
  }

  renderRates(rates, destination) {
    if (!rates || rates.length === 0) {
      this.showError('Nenhuma opção de entrega encontrada para este CEP.');
      return;
    }

    let html = `
      <div class="shipping-calc__destination">
        Destino: ${destination}
      </div>
      <table class="shipping-results-table"><tbody>
    `;

    rates.forEach(rate => {
      const formattedPrice = this.formatMoney(rate.price);

      // Clean up delivery days (fix for "20,20" bug from some apps)
      let deliveryDaysText = '';
      if (rate.delivery_days) {
        let daysStr = String(rate.delivery_days);
        if (daysStr.includes(',')) {
          daysStr = daysStr.split(',')[0];
        }
        deliveryDaysText = `<br><small>Entrega em ${daysStr} dias úteis</small>`;
      }

      html += `
        <tr>
          <td>
            <strong>${rate.name}</strong>
            ${deliveryDaysText}
          </td>
          <td class="shipping-result__price">${formattedPrice}</td>
        </tr>
      `;
    });

    html += '</tbody></table>';
    this.resultsContainer.innerHTML = html;
    this.resultsContainer.removeAttribute('hidden');
  }

  showError(message) {
    this.resultsContainer.innerHTML = `<div class="shipping-calc__error">${message}</div>`;
    this.resultsContainer.removeAttribute('hidden');
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.button.classList.add('loading');
      this.button.setAttribute('disabled', 'disabled');
      this.button.querySelector('.btn-text').textContent = ' Calculando...';
    } else {
      this.button.classList.remove('loading');
      this.button.removeAttribute('disabled');
      this.button.querySelector('.btn-text').textContent = 'Calcular';
    }
  }

  formatMoney(priceInCents) {
    // 2500 -> R$ 25,00
    const price = (parseFloat(priceInCents)).toFixed(2).replace('.', ',');
    return `R$ ${price.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  new ShippingCalculator();
});
